import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { BookingsRestaurantComponent } from '../bookings-restaurant/bookings-restaurant.component';
import { ConsumptionRestaurantComponent } from '../consumption-restaurant/consumption-restaurant.component';
import { CalendarRestaurantComponent } from '../calendar-restaurant/calendar-restaurant.component';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, Subject, takeUntil, Subscription } from 'rxjs';
import { RestaurantService } from '../../../shared/services/restaurant/restaurant.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { RestaurantConfig } from 'src/app/shared/interfaces/restaurant.interface';
import { RestaurantSendMenusDialogComponent } from 'src/app/shared/components/restaurant/restaurant-send-menus-dialog/restaurant-send-menus-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-restaurant',
  imports: [
    // MatIcon,
    MatButtonModule,
    MatButton,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatDialogModule,
    MatMenuModule,
    MatPaginatorModule,
    TitleComponent,
    MatTabsModule,
    MatDatepickerModule,
    MatInputModule,
    BookingsRestaurantComponent,
    ConsumptionRestaurantComponent,
    CalendarRestaurantComponent,
    RouterOutlet,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './restaurant.component.html',
  styleUrls: [
    './restaurant.component.css',
    '../../../shared/styles/layout.css',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RestaurantComponent implements OnDestroy {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private restaurantService: RestaurantService,
    private rolesService: RolesService,
    private storageService: StorageService
  ) {}

  readonly dialog = inject(MatDialog);
  private destroy$ = new Subject<void>();
  private navigationSubscription: Subscription | null = null;
  myroles: any = {}; // Change from array to object

  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;
  selectedTabIndex: number = 0;

  title: string = 'Comedor';
  subtitle: string =
    'Consulta los detalles generales de las reservas al comedor.';

  total1: string = 'MENUS RESERVADOS TOTALES';
  total2: string = 'MENUS CONSUMIDOS TOTALES';
  number1: number = 0;
  number2: number = 0;

  // Add property to hold reservation stats from child
  reservationStats: any = null;

  // Add property to track extra menus from child component
  currentExtraMenus: number = 0;

  config: RestaurantConfig = {
    id: 0,
    extra_menus: 0,
    start_date: '',
    end_date: null,
  };

  // Add MatSnackBar injection
  private snackBar = inject(MatSnackBar);

  goToAddReservation() {
    this.router.navigate(['/home/dining/general/reservation-form']);
  }

  goToEdit() {
    this.router.navigate(['/home/dining/general/edit-date']);
  }

  goToMenu() {
    this.router.navigate(['/home/dining/general/menu-edit']);
  }

  goToReservationConfig() {
    this.router.navigate(['/home/dining/general/reservation-config']);
  }

  getConfig() {
    // Get config for today's date
    const today = new Date();
    const dateString = this.formatYYYYMMDD(today);

    this.restaurantService
      .getRestaurantConfig(dateString)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (config) => {
          this.config = config;
        },
        error: (err) => {
          console.error('Error fetching restaurant config:', err);
        },
      });
  }

  private formatYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  isEditDatePage = false;

  loadRoles() {
    // Use the refactored method in RolesService
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'general',
      this.storageService
    );
    if (permissions) {
      this.myroles = permissions;
    } else {
      //  console.log(
      //    'No user roles found in storage or no permissions for Comedor'
      //  );
    }
  }

  ngOnInit() {
    // Subscribe to refresh stats events - don't check the value since it's void now
    // this.restaurantService
    //   .getRefreshStatsObservable()
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe(() => {
    //     this.loadGlobalStats();
    //   });

    this.loadRoles();
    // this.getConfig();
    this.setupNavigationListener();

    // Read query parameter to set active tab
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const tab = params['tab'];
      if (tab === 'consumos') {
        this.selectedTabIndex = 1;
      } else if (tab === 'calendario') {
        this.selectedTabIndex = 2;
      } else {
        this.selectedTabIndex = 0;
      }
      this.cdr.detectChanges();
    });
  }

  setupNavigationListener() {
    // Clean up existing navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }

    // Set up new navigation subscription
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (
          this.router.url.includes('/dining/general/edit-date') ||
          this.router.url.includes('/dining/general/menu-edit') ||
          this.router.url.includes('/dining/general/reservation-form') ||
          this.router.url.includes('/dining/general/reservation-config')
        ) {
          this.isEditDatePage = true;
        } else if (this.router.url === '/home/dining/general') {
          this.isEditDatePage = false;
        }

        this.cdr.detectChanges();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  // Add handler for reservationStatsChange event
  onReservationStatsChange(stats: any) {
    this.reservationStats = stats;
    // Update card numbers
    this.number1 = stats?.total_reservations ?? 0;
    this.number2 = stats?.total_normal_reservations ?? 0;
    this.cdr.detectChanges();
  }

  // Add handler for extraMenusChange event
  onExtraMenusChange(extraMenus: number) {
    this.currentExtraMenus = extraMenus;
    this.cdr.detectChanges();
  }

  openCateringSendingPopup() {
    let dialogRef = this.dialog.open(RestaurantSendMenusDialogComponent, {
      height: '505px',
      width: '600px',
    });
  }
}
